import * as Sentry from "@sentry/react";

import { indexedDbAvailable } from "@/db/indexedDbAvailable.ts";
import { createLogger } from "@/logger";

const log = createLogger("storage");

const TEST_DB_NAME = "__storage_check__";
const TEST_OPEN_TIMEOUT_MS = 5000;
const MIN_FREE_BYTES = 50 * 1024 * 1024;

export type StorageAvailability =
  | { available: true }
  | {
      available: false;
      reason: string;
      details?: Record<string, unknown>;
    };

/**
 * Pre-flight check that surfaces IndexedDB / storage problems before the app
 * tries to open its real PGlite database. We treat any of the following as a
 * hard failure so the UI can show a recovery path instead of crashing later:
 *   - WebAssembly API missing (e.g. Safari Private Browsing strips the global)
 *   - IndexedDB API missing (e.g. private mode in some browsers)
 *   - `WebAssembly` or `crypto.subtle` missing — Safari Lockdown Mode strips
 *     these while still exposing `indexedDB`, so PGlite would otherwise reach
 *     its eager `pglite.wasm` / `initdb.wasm` prefetch and emit an unhandled
 *     `TypeError: Load failed` (WEB-1995 / SBV2-WEB-1WX, sibling WEB-1997).
 *   - StorageManager reports < MIN_FREE_BYTES of headroom
 *   - A canary `indexedDB.open` cannot complete within the timeout window
 *
 * The WebAssembly probe matters because PGlite's `_init` evaluates
 * `new WebAssembly.Memory(...)` synchronously inside a detached promise chain;
 * a missing `WebAssembly` global throws a `ReferenceError` that escapes the
 * `try/catch` around `PGlite.create` and surfaces as an unhandled rejection.
 *
 * Capability probes use `typeof` so the references themselves never throw
 * `ReferenceError` in locked-down runtimes where the global may be removed.
 */
export async function checkStorageAvailability(): Promise<StorageAvailability> {
  const wasm = globalThis.WebAssembly;
  if (typeof wasm === "undefined" || typeof wasm.Memory !== "function") {
    return { available: false, reason: "WebAssembly not supported" };
  }

  if (!indexedDbAvailable()) {
    return { available: false, reason: "IndexedDB not supported" };
  }

  const webCrypto = globalThis.crypto;
  if (typeof webCrypto === "undefined" || !webCrypto.subtle) {
    return { available: false, reason: "Web Crypto (crypto.subtle) not supported" };
  }

  let quotaUsed: number | undefined;
  let quotaTotal: number | undefined;

  try {
    if (navigator.storage?.estimate) {
      const estimate = await navigator.storage.estimate();
      quotaUsed = estimate.usage ?? 0;
      quotaTotal = estimate.quota ?? 0;

      if (quotaTotal > 0 && quotaTotal - quotaUsed < MIN_FREE_BYTES) {
        const availableBytes = quotaTotal - quotaUsed;
        const availableMb = Math.round(availableBytes / 1024 / 1024);
        return {
          available: false,
          reason: `Insufficient storage space (${availableMb}MB available)`,
          details: { quotaUsed, quotaTotal, availableBytes },
        };
      }
    }
  } catch (error) {
    log.warn("[StorageCheck] Could not query storage quota", { error });
  }

  try {
    const db = await openCanaryDb();
    db.close();
    await deleteCanaryDb();
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);
    return {
      available: false,
      reason: `IndexedDB test failed: ${reason}`,
      details: { quotaUsed, quotaTotal },
    };
  }

  return { available: true };
}

async function openCanaryDb(): Promise<IDBDatabase> {
  return await new Promise<IDBDatabase>((resolve, reject) => {
    let settled = false;
    const settle = (action: () => void): void => {
      if (settled) return;
      settled = true;
      action();
    };

    const timer = setTimeout(() => settle(() => reject(new Error("IndexedDB open timeout"))), TEST_OPEN_TIMEOUT_MS);
    const request = window.indexedDB.open(TEST_DB_NAME, 1);
    request.onsuccess = () => {
      clearTimeout(timer);
      if (settled) {
        request.result.close();
        return;
      }
      settle(() => resolve(request.result));
    };
    request.onerror = () => {
      clearTimeout(timer);
      settle(() => reject(request.error ?? new Error("IndexedDB open error")));
    };
    request.onblocked = () => {
      clearTimeout(timer);
      settle(() => reject(new Error("IndexedDB blocked")));
    };
  });
}

async function deleteCanaryDb(): Promise<void> {
  await new Promise<void>((resolve) => {
    const request = window.indexedDB.deleteDatabase(TEST_DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}

/**
 * Push storage availability findings to Sentry so we can compare the breakdown
 * of availability errors with the existing runStorageProbe latency context.
 */
export function reportStorageUnavailable(result: Extract<StorageAvailability, { available: false }>): void {
  Sentry.setContext("storage_availability", {
    reason: result.reason,
    ...(result.details ?? {}),
  });
  Sentry.captureMessage(`Storage unavailable: ${result.reason}`, "warning");
  log.error("[StorageCheck] Storage unavailable", {
    reason: result.reason,
    details: result.details,
  });
}
